import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { User, Pencil, History, Phone, Mail, MapPin, CreditCard, ShieldCheck, Fingerprint, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getUser } from '@/lib/auth';
import api from '@/lib/api';

const FIELD_ICONS = {
  phone: Phone, alternate_phone: Phone, personal_email: Mail,
  emergency_contact_name: User, emergency_contact_phone: Phone,
  emergency_contact_relationship: User,
  current_address: MapPin, permanent_address: MapPin,
  blood_group: ShieldCheck, date_of_birth: User,
  bank_name: CreditCard, bank_account_number: CreditCard,
  bank_ifsc: CreditCard, pan_number: CreditCard,
};

const FIELD_GROUPS = [
  { title: 'Personal Information', icon: User, fields: ['phone', 'alternate_phone', 'personal_email', 'date_of_birth', 'blood_group'] },
  { title: 'Emergency Contact', icon: Phone, fields: ['emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship'] },
  { title: 'Address', icon: MapPin, fields: ['current_address', 'permanent_address'] },
  { title: 'Bank & Tax Details', icon: CreditCard, fields: ['bank_name', 'bank_account_number', 'bank_ifsc', 'pan_number'] },
];

export default function SelfServicePage() {
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [registering, setRegistering] = useState(false);
  const currentUser = getUser();

  const load = async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get('/api/self-service/profile'),
        api.get('/api/self-service/history'),
      ]);
      setProfile(p.data);
      setHistory(h.data);
      try {
        const c = await api.get('/api/webauthn/credentials');
        setCredentials(c.data);
      } catch {}
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openEdit = (field, label) => {
    const current = profile?.employee?.[field] || '';
    setEditField({ field, label });
    setEditValue(current);
    setEditReason('');
  };

  const handleSave = async () => {
    if (!editField) return;
    setSaving(true);
    try {
      await api.post('/api/self-service/update', {
        field: editField.field,
        value: editValue,
        reason: editReason,
      });
      toast.success(`${editField.label} updated`);
      setEditField(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  const handleRegisterBiometric = async () => {
    setRegistering(true);
    try {
      const { data: options } = await api.post('/api/webauthn/register/options');
      const publicKey = {
        challenge: Uint8Array.from(atob(options.challenge.replace(/-/g,'+').replace(/_/g,'/') + '=='.slice(0,(4-options.challenge.length%4)%4)), c=>c.charCodeAt(0)),
        rp: options.rp,
        user: {
          id: Uint8Array.from(atob(options.user.id.replace(/-/g,'+').replace(/_/g,'/') + '=='.slice(0,(4-options.user.id.length%4)%4)), c=>c.charCodeAt(0)),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        authenticatorSelection: options.authenticatorSelection,
        timeout: options.timeout,
        attestation: options.attestation,
      };
      const credential = await navigator.credentials.create({ publicKey });
      const b64url = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
      await api.post('/api/webauthn/register/verify', {
        id: credential.id,
        rawId: b64url(credential.rawId),
        response: {
          clientDataJSON: b64url(credential.response.clientDataJSON),
          attestationObject: b64url(credential.response.attestationObject),
        },
        type: credential.type,
        deviceName: navigator.userAgent.includes('iPhone') ? 'iPhone' : navigator.userAgent.includes('Android') ? 'Android' : 'Device',
      });
      toast.success('Biometric login registered!');
      load();
    } catch (e) {
      if (e.name === 'NotAllowedError') toast.error('Biometric registration cancelled');
      else toast.error(e.response?.data?.detail || 'Registration failed');
    }
    setRegistering(false);
  };

  const handleDeleteCredential = async (credId) => {
    try {
      await api.delete(`/api/webauthn/credentials/${encodeURIComponent(credId)}`);
      toast.success('Credential removed');
      load();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const emp = profile?.employee || {};
  const editableFields = profile?.editable_fields || {};

  return (
    <div className="space-y-6" data-testid="self-service-page">
      <div>
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// MY PROFILE</Badge>
        <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Self Service Portal</h1>
        <p className="text-sm text-slate-500">View and update your personal information</p>
      </div>

      {loading ? <Skeleton className="h-96" /> : (
        <Tabs defaultValue="profile">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="profile">My Profile</TabsTrigger>
            <TabsTrigger value="history">Change History</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4 space-y-4">
            {/* Employee Header */}
            <Card className="border-slate-200">
              <CardContent className="py-5 px-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#0D2850] flex items-center justify-center text-white text-xl font-bold">{emp.name?.charAt(0) || '?'}</div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-[#0B1B33]">{emp.name}</h2>
                    <p className="text-sm text-slate-500">{emp.designation} - {emp.department}</p>
                    <div className="flex gap-3 mt-1 text-xs text-slate-400">
                      <span>{emp.employee_code}</span>
                      <span>{emp.location}</span>
                      <span>Joined: {emp.date_of_joining || 'N/A'}</span>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{emp.status}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Editable Field Groups */}
            {FIELD_GROUPS.map(group => (
              <Card key={group.title} className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <group.icon className="w-4 h-4 text-[#0D2850]" />{group.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {group.fields.map(field => {
                    const Icon = FIELD_ICONS[field] || User;
                    const label = editableFields[field] || field;
                    const value = emp[field] || '';
                    return (
                      <div key={field} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-slate-500">{label}</p>
                            <p className="text-sm font-medium text-[#0B1B33] truncate" data-testid={`field-${field}`}>
                              {value || <span className="text-slate-300 italic">Not set</span>}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="shrink-0 text-[#0D2850]" onClick={() => openEdit(field, label)} data-testid={`edit-${field}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <History className="w-4 h-4 text-[#0D2850]" />Change History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {history.length === 0 ? (
                  <div className="p-12 text-center"><History className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">No changes yet</p></div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50"><TableHead>Field</TableHead><TableHead>Old Value</TableHead><TableHead>New Value</TableHead><TableHead>Reason</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {history.map((h, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium">{h.field_label}</TableCell>
                            <TableCell className="text-sm text-red-500 line-through">{h.old_value || '-'}</TableCell>
                            <TableCell className="text-sm text-emerald-600 font-medium">{h.new_value}</TableCell>
                            <TableCell className="text-sm text-slate-500">{h.reason || '-'}</TableCell>
                            <TableCell className="text-xs text-slate-400">{new Date(h.changed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="mt-4 space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Fingerprint className="w-4 h-4 text-[#0D2850]" />Biometric Login
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">Register your fingerprint or Face ID for quick, passwordless login on this device.</p>
                {('credentials' in navigator && 'PublicKeyCredential' in window) ? (
                  <Button className="bg-[#0D2850] hover:bg-[#0B2246] gap-2" onClick={handleRegisterBiometric} disabled={registering} data-testid="register-biometric-btn">
                    <Fingerprint className="w-4 h-4" />{registering ? 'Registering...' : 'Register Biometric'}
                  </Button>
                ) : (
                  <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">Biometric login is not supported on this browser. Try using Chrome, Safari, or Edge on a device with biometric hardware.</p>
                )}
                {credentials.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase">Registered Devices</h4>
                    {credentials.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Fingerprint className="w-5 h-5 text-emerald-600" />
                          <div>
                            <p className="text-sm font-medium">{c.device_name || 'Device'}</p>
                            <p className="text-xs text-slate-400">Registered: {new Date(c.created_at).toLocaleDateString('en-IN')}{c.last_used ? ` | Last used: ${new Date(c.last_used).toLocaleDateString('en-IN')}` : ''}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteCredential(c.credential_id)} data-testid={`delete-cred-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editField} onOpenChange={() => setEditField(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Edit {editField?.label}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">{editField?.label}</Label>
              <Input value={editValue} onChange={e => setEditValue(e.target.value)} data-testid="edit-field-input" autoFocus />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Reason for change (optional)</Label>
              <Input value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="e.g., New phone number" data-testid="edit-reason-input" />
            </div>
            <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleSave} disabled={saving} data-testid="save-field-btn">
              {saving ? 'Saving...' : 'Save Change'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
